import { useNavigate } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'

export interface StoriesPerDayProps {
  data: Array<{ date: string; count: number }>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

/**
 * Stories-per-day trend chart for the SP4 story-first Analytics page
 * (spec §4.2 ④, plan T20).
 *
 * Renders a `recharts` AreaChart using the `--chart-primary` palette. The
 * whole chart is clickable: `onClick` on the `<AreaChart>` reads
 * `activePayload[0].payload.date` and navigates to
 * `/stories?published_from=YYYY-MM-DD&published_to=YYYY-MM-DD` (1-day
 * window, same date).
 *
 * A keyboard-accessible row of day buttons is also rendered below the
 * chart so screen-reader and keyboard users can activate the same
 * navigation, and so tests can click a deterministic target (recharts
 * SVG hit-testing is unreliable under jsdom).
 */
export function StoriesPerDay({
  data,
  isLoading,
  isError,
  onRetry,
}: StoriesPerDayProps) {
  const navigate = useNavigate()

  const go = (date: string) => {
    navigate(
      `/stories?published_from=${date}&published_to=${date}`,
    )
  }

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Stories per day</h3>
        <Skeleton
          data-testid="sp4-stories-per-day-skeleton"
          className="h-48 w-full"
        />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Stories per day</h3>
        <ErrorState
          title="Couldn't load stories-per-day data."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Stories per day</h3>
        <p className="text-sm text-muted-foreground">
          No stories in the selected range.
        </p>
      </Card>
    )
  }

  return (
    <Card
      data-testid="sp4-stories-per-day"
      className="p-4"
    >
      <h3 className="text-sm font-semibold mb-3">Stories per day</h3>
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
            onClick={(next) => {
              const payload = next?.activePayload?.[0]?.payload as
                | { date?: string }
                | undefined
              if (payload?.date) {
                go(payload.date)
              }
            }}
          >
            <defs>
              <linearGradient
                id="sp4-stories-per-day-fill"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor="var(--chart-primary)"
                  stopOpacity={0.35}
                />
                <stop
                  offset="95%"
                  stopColor="var(--chart-primary)"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--chart-grid-stroke)"
            />
            <XAxis
              dataKey="date"
              tick={{
                fontSize: 11,
                fill: 'var(--chart-axis-label)',
              }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{
                fontSize: 11,
                fill: 'var(--chart-axis-label)',
              }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--surface-elevated)',
                border: '1px solid var(--border-default)',
                borderRadius: '0.5rem',
                fontSize: '0.75rem',
              }}
              formatter={(v: number) => [v, 'Stories']}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke="var(--chart-primary-strong)"
              strokeWidth={2}
              fill="url(#sp4-stories-per-day-fill)"
              dot={{ r: 3, fill: 'var(--chart-primary-strong)' }}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Keyboard- and screen-reader-accessible day buckets.
          Visually hidden by default; screen readers and keyboard users
          can still navigate through them, and unit tests click them
          directly (recharts SVG hit-testing is unreliable under jsdom). */}
      <div
        role="group"
        aria-label="Stories per day, by bucket"
        className="sr-only"
      >
        {data.map((d) => (
          <button
            key={d.date}
            type="button"
            data-testid={`sp4-stories-per-day-bucket-${d.date}`}
            onClick={() => go(d.date)}
          >
            {`${d.date}: ${d.count} stories`}
          </button>
        ))}
      </div>
    </Card>
  )
}
